import { API_URL, REQUEST_TIMEOUT_MS } from "../../config";
import { TokenService } from "../tokenService";
import { debugLog } from "../terminalLogger";
import { corelCommunication } from "../corel/communication";
import { getTenantId } from "../../utils/getTenantId";

/**
 * Dados do início de uma transferência de upload de layout, enviados do host (C#) para o WebView.
 * Contém metadados do pedido (ORC/OIT), identificadores e informações do arquivo que será enviado.
 */
type BeginData = {
	transferId: string;
	meta: {
		orc_id: number;
		oit_id: number;
		staticId: string;
		escala?: number;
		resolucao_dpi?: number;
		transparencia?: boolean;
		compressao?: string;
		source_host?: string;
	};
	totalBytes: number;
	/** MIME do arquivo final reconstruído. Aceita apenas image/png e image/jpeg */
	mime: "image/png" | "image/jpeg";
	/** Tamanho aproximado do arquivo informado na origem (pode ser usado para validações rápidas). */
	sizeBytes: number;
	/** Hash SHA-256 (hex) opcional para verificação de integridade ponta-a-ponta. */
	sha256?: string; // hex
	/** Chave de idempotência opcional. Se ausente, um fallback será gerado no handleBegin. */
	idempotencyKey?: string; // vindo do C#; se faltar, geramos fallback
	/** Identifica a aplicação de origem deste fluxo. */
	source_app?: "coreldraw-webview";
};

/**
 * Representa um pedaço (chunk) de dados em Base64 que compõe o arquivo a ser enviado.
 * O seq inicia em 1 e a ordem é preservada pelo receptor.
 */
type ChunkData = {
	transferId: string;
	/** Sequência do chunk iniciando em 1. */
	seq: number; // começa em 1
	/** Total de chunks esperados (opcional; pode chegar apenas no primeiro pacote). */
	totalChunks?: number;
	/** Dados em Base64 do chunk. */
	dataB64: string;
};

/**
 * Mensagem sinalizando o fim do envio de chunks para uma determinada transferência.
 */
type EndData = {
	transferId: string;
	/** Tamanho total (em bytes) anunciado para validação no receptor. */
	totalBytes: number;
	/** Hash SHA-256 (hex) opcional para revalidação de integridade ao final. */
	sha256?: string; // hex
};

/**
 * Estrutura usada internamente para rastrear uma transferência em andamento (inflight).
 */
type Inflight = {
	/** Metadados recebidos no início. */
	begin: BeginData;
	/** Buffer dos chunks na ordem correta. Índices sem valor indicam lacunas. */
	chunks: (string | undefined)[];
	/** Quantidade esperada de chunks, quando informado. */
	expectedChunks?: number;
	/** Estimativa do total de bytes recebidos (considerando Base64). */
	receivedBytesApprox: number;
	/** Timestamp de início, em ms, via performance.now(). */
	startedAt: number;
};

const inflight = new Map<string, Inflight>();

// Configurações (com defaults sensatos; podem ser ajustadas por ENV via config.ts no futuro)
const MAX_FILE_SIZE_MB = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB ?? 10);
const UPLOAD_TIMEOUT_MS = Number(
	import.meta.env.VITE_UPLOAD_TIMEOUT_MS ?? 600000,
); // 10 min
const MAX_PARALLEL_UPLOADS = Number(
	import.meta.env.VITE_MAX_PARALLEL_UPLOADS ?? 2,
);

// Fila simples para controlar concorrência de uploads prontos
const uploadQueue: Array<() => Promise<void>> = [];
let activeUploads = 0;

/**
 * Agenda uma tarefa de upload para execução com limitação de concorrência.
 * As tarefas são executadas de acordo com MAX_PARALLEL_UPLOADS.
 */
function scheduleUpload(task: () => Promise<void>) {
	uploadQueue.push(task);
	runNext();
}

/**
 * Executa a próxima tarefa da fila, respeitando o limite de uploads paralelos.
 */
function runNext() {
	if (activeUploads >= MAX_PARALLEL_UPLOADS) return;
	const next = uploadQueue.shift();
	if (!next) return;
	activeUploads++;
	next()
		.catch(() => {})
		.finally(() => {
			activeUploads--;
			runNext();
		});
}

/**
 * Lista de MIME types permitidos para upload
 */
const ALLOWED_MIME_TYPES = ["image/png", "image/jpeg"] as const;
type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

// Constante ALLOWED_EXTENSIONS removida - não estava sendo utilizada
// Se necessário no futuro, pode ser reimplementada

/**
 * Verifica se o MIME informado é permitido para upload de layout.
 */
function isAllowedMime(m: string): m is AllowedMimeType {
	return ALLOWED_MIME_TYPES.includes(m as AllowedMimeType);
}

// Função removida - não estava sendo utilizada
// Se necessário no futuro, pode ser reimplementada

/**
 * Valida o nome do arquivo para prevenir path traversal e caracteres perigosos
 */
function validateFilename(filename: string): boolean {
	if (!filename || filename.length === 0) return false;
	if (filename.length > 255) return false;

	// Previne path traversal
	if (
		filename.includes("..") ||
		filename.includes("/") ||
		filename.includes("\\")
	)
		return false;

	// Permite apenas caracteres seguros
	const safePattern = /^[a-zA-Z0-9._-]+$/;
	return safePattern.test(filename);
}

/**
 * Valida o tamanho do arquivo
 */
function validateFileSize(sizeBytes: number): boolean {
	const maxSizeBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
	return sizeBytes > 0 && sizeBytes <= maxSizeBytes;
}

/**
 * Calcula o hash SHA-256 de um ArrayBuffer e retorna no formato hexadecimal.
 */
async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
	const digest = await crypto.subtle.digest("SHA-256", buffer);
	return Array.from(new Uint8Array(digest))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
}

/**
 * Converte uma string Base64 em um Uint8Array.
 */
function b64ToUint8Array(b64: string): Uint8Array {
	const bin = atob(b64);
	const len = bin.length;
	const arr = new Uint8Array(len);
	for (let i = 0; i < len; i++) arr[i] = bin.charCodeAt(i);
	return arr;
}

/**
 * Realiza o upload do arquivo reconstruído para a API, com suporte a idempotência
 * e retentativas com backoff exponencial para falhas transitórias (5xx) e conflito (409).
 */
async function uploadToApi(begin: BeginData, blob: Blob): Promise<Response> {
	const form = new FormData();
	const ext = begin.mime === "image/png" ? "png" : "jpg";
	const filename = `${begin.meta.staticId}_${crypto.randomUUID()}.${ext}`;
	form.append("file", blob, filename);
	form.append("orc_id", String(begin.meta.orc_id));
	form.append("oit_id", String(begin.meta.oit_id));
	form.append("staticId", begin.meta.staticId);
	if (begin.meta.escala != null)
		form.append("escala", String(begin.meta.escala));
	if (begin.meta.resolucao_dpi != null)
		form.append("resolucao_dpi", String(begin.meta.resolucao_dpi));
	if (begin.meta.transparencia != null)
		form.append("transparencia", String(begin.meta.transparencia));
	if (begin.meta.compressao) form.append("compressao", begin.meta.compressao);
	if (begin.sha256) form.append("sha256", begin.sha256);
	form.append("source_app", "coreldraw-webview");

	const token = TokenService.getAccessToken();

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);

	const doFetch = async (
		attempt: number,
		idemKey: string,
	): Promise<Response> => {
		try {
			// Log de request para o terminal: apenas metadados (sem arquivo)
			try {
				debugLog("API_REQUEST", {
					method: "POST",
					url: "/arquivos/layouts",
					payload: {
						meta: begin.meta,
						filename: `${begin.meta.staticId}.${
							begin.mime === "image/png" ? "png" : "jpg"
						}`,
						sizeBytes: begin.sizeBytes,
					},
				});
			} catch {}
			const tenantId = getTenantId();
			const res = await fetch(`${API_URL}/arquivos/layouts`, {
				method: "POST",
				body: form,
				headers: {
					...(token ? { Authorization: `Bearer ${token}` } : {}),
					...(idemKey ? { "Idempotency-Key": idemKey } : {}),
					...(tenantId ? { "X-Tenant-Id": tenantId } : {}),
				},
				signal: controller.signal,
				mode: "cors",
				credentials: "include",
			});

			if (res.status >= 500 && res.status < 600 && attempt < 3) {
				const backoff =
					500 * Math.pow(2, attempt) + Math.floor(Math.random() * 200);
				await new Promise((r) => setTimeout(r, backoff));
				return doFetch(attempt + 1, idemKey);
			}

			// 409: gerar nova Idempotency-Key e tentar 1 vez
			if (res.status === 409 && attempt < 1) {
				const newKey = crypto.randomUUID();
				debugLog("UPLOAD_RETRY_IDEMPOTENCY", {
					from: begin.idempotencyKey,
					to: newKey,
				});
				const backoff = 500 + Math.floor(Math.random() * 200);
				await new Promise((r) => setTimeout(r, backoff));
				return doFetch(attempt + 1, newKey);
			}

			return res;
		} catch (err) {
			if (attempt < 3) {
				const backoff =
					500 * Math.pow(2, attempt) + Math.floor(Math.random() * 200);
				await new Promise((r) => setTimeout(r, backoff));
				return doFetch(
					attempt + 1,
					begin.idempotencyKey || crypto.randomUUID(),
				);
			}
			throw err;
		}
	};

	try {
		const res = await doFetch(0, begin.idempotencyKey || crypto.randomUUID());
		return res;
	} finally {
		clearTimeout(timeout);
	}
}

/**
 * Processa a transferência completa: valida consistência, reconstrói o arquivo,
 * verifica hash opcional, e inicia o upload para a API. Notifica o host com o resultado.
 */
async function processCompleteTransfer(transferId: string) {
	const t = inflight.get(transferId);
	if (!t) return;

	// Verificar completude
	if (t.chunks.some((x) => !x)) {
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId,
			status: "error",
			apiStatus: 400,
			message: "Chunks ausentes",
		});
		inflight.delete(transferId);
		return;
	}

	// Reconstituir
	const arrays: Uint8Array[] = [];
	for (const b64 of t.chunks) {
		arrays.push(b64ToUint8Array(b64 as string));
	}
	const totalLen = arrays.reduce((acc, a) => acc + a.byteLength, 0);
	if (totalLen !== t.begin.totalBytes) {
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId,
			status: "error",
			apiStatus: 400,
			message: "Tamanho inconsistente",
		});
		inflight.delete(transferId);
		return;
	}
	const merged = new Uint8Array(totalLen);
	let offset = 0;
	for (const part of arrays) {
		merged.set(part, offset);
		offset += part.byteLength;
	}

	// Verificar MIME/tamanho
	if (!isAllowedMime(t.begin.mime)) {
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId,
			status: "error",
			apiStatus: 415,
			message: "MIME não permitido",
		});
		inflight.delete(transferId);
		return;
	}
	const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
	if (totalLen <= 0 || totalLen > maxBytes) {
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId,
			status: "error",
			apiStatus: 413,
			message: "Arquivo muito grande",
		});
		inflight.delete(transferId);
		return;
	}

	// Verificar SHA-256, se fornecido
	if (t.begin.sha256) {
		try {
			const hex = await sha256Hex(merged.buffer);
			if (hex.toLowerCase() !== t.begin.sha256.toLowerCase()) {
				corelCommunication.sendActionGuarded("uploadLayoutResult", {
					transferId,
					status: "error",
					apiStatus: 400,
					message: "SHA256_MISMATCH",
					diagnostics: { expected: t.begin.sha256, received: hex },
				});
				inflight.delete(transferId);
				return;
			}
		} catch (e) {
			debugLog("SHA256_CALC_ERROR", { transferId, error: (e as any)?.message });
		}
	}

	const blob = new Blob([merged], { type: t.begin.mime });
	const started = performance.now();
	try {
		const res = await uploadToApi(t.begin, blob);
		const latencyMs = Math.round(performance.now() - started);
		let body: any = {};
		try {
			body = await res.json();
		} catch {}

		debugLog("UPLOAD_RESULT", {
			transferId,
			orc_id: t.begin.meta.orc_id,
			oit_id: t.begin.meta.oit_id,
			staticId: t.begin.meta.staticId,
			mime: t.begin.mime,
			size: totalLen,
			sha256: t.begin.sha256,
			idempotencyKey: t.begin.idempotencyKey,
			latencyMs,
			status: res.status,
			idempotent: body?.idempotent === true,
		});

		if (res.ok) {
			corelCommunication.sendActionGuarded("uploadLayoutResult", {
				transferId,
				status: "success",
				apiStatus: res.status,
				resourceId: body?.data?.export_id ?? body?.export_id ?? body?.id,
				arquivoId: body?.data?.arquivo_id ?? body?.arquivo_id,
				idempotent: body?.idempotent === true,
				httpCode: res.status,
			});
		} else {
			corelCommunication.sendActionGuarded("uploadLayoutResult", {
				transferId,
				status: "error",
				apiStatus: res.status,
				message: body?.error?.message || body?.message || "Falha no upload",
				httpCode: res.status,
				diagnostics: body,
			});
		}
	} catch (error: any) {
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId,
			status: "error",
			apiStatus: 0,
			message: error?.message || "Erro de rede/timeout",
			diagnostics: { kind: "network" },
		});
	} finally {
		inflight.delete(transferId);
	}
}

/**
 * Trata a mensagem de início de upload, validando MIME e tamanho e inicializando o estado inflight.
 * Também define uma chave de idempotência de fallback caso não seja fornecida.
 */
function handleBegin(data: BeginData) {
	// Validação de MIME type
	if (!isAllowedMime(data.mime)) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_mime",
			mime: data.mime,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 415,
			message: "MIME não permitido",
		});
		return;
	}

	// Validação de tamanho do arquivo
	if (!validateFileSize(data.sizeBytes)) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_size",
			sizeBytes: data.sizeBytes,
			maxSizeMB: MAX_FILE_SIZE_MB,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 413,
			message: "Arquivo muito grande",
		});
		return;
	}

	// Validação do staticId (usado para nome do arquivo)
	if (!data.meta.staticId || !validateFilename(data.meta.staticId)) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_static_id",
			staticId: data.meta.staticId,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "ID estático inválido",
		});
		return;
	}

	// Validação de IDs numéricos
	if (
		!Number.isInteger(data.meta.orc_id) ||
		data.meta.orc_id <= 0 ||
		!Number.isInteger(data.meta.oit_id) ||
		data.meta.oit_id <= 0
	) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_ids",
			orc_id: data.meta.orc_id,
			oit_id: data.meta.oit_id,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "IDs inválidos",
		});
		return;
	}

	// Validação de parâmetros opcionais
	if (
		data.meta.escala !== undefined &&
		(typeof data.meta.escala !== "number" ||
			data.meta.escala <= 0 ||
			data.meta.escala > 1000)
	) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_escala",
			escala: data.meta.escala,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "Escala inválida",
		});
		return;
	}

	if (
		data.meta.resolucao_dpi !== undefined &&
		(typeof data.meta.resolucao_dpi !== "number" ||
			data.meta.resolucao_dpi < 72 ||
			data.meta.resolucao_dpi > 600)
	) {
		debugLog("UPLOAD_VALIDATION_ERROR", {
			transferId: data.transferId,
			error: "invalid_dpi",
			resolucao_dpi: data.meta.resolucao_dpi,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "DPI inválido",
		});
		return;
	}

	// Fallback de idempotência, se ausente
	if (!data.idempotencyKey) {
		data.idempotencyKey = `${crypto.randomUUID()}-${(data.sha256 || "").slice(
			0,
			12,
		)}`;
		debugLog("IDEMPOTENCY_FALLBACK", {
			transferId: data.transferId,
			idempotencyKey: data.idempotencyKey,
			source: "idempotency-fallback",
		});
	}

	debugLog("UPLOAD_BEGIN_VALIDATED", {
		transferId: data.transferId,
		mime: data.mime,
		sizeBytes: data.sizeBytes,
		staticId: data.meta.staticId,
	});
	inflight.set(data.transferId, {
		begin: data,
		chunks: [],
		expectedChunks: undefined,
		receivedBytesApprox: 0,
		startedAt: performance.now(),
	});
}

/**
 * Trata a chegada de um chunk, armazenando-o na posição correta e enviando um ACK ao host.
 */
function handleChunk(data: ChunkData) {
	const t = inflight.get(data.transferId);
	if (!t) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "transfer_not_found",
			seq: data.seq,
		});
		return;
	}

	// Validação do número de sequência
	if (!Number.isInteger(data.seq) || data.seq < 1) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "invalid_seq",
			seq: data.seq,
		});
		return;
	}

	// Validação do totalChunks se fornecido
	if (
		data.totalChunks !== undefined &&
		(!Number.isInteger(data.totalChunks) ||
			data.totalChunks < 1 ||
			data.totalChunks > 10000)
	) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "invalid_total_chunks",
			totalChunks: data.totalChunks,
		});
		return;
	}

	// Validação do base64
	if (!data.dataB64 || typeof data.dataB64 !== "string") {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "invalid_data_b64",
			seq: data.seq,
		});
		return;
	}

	// Validação básica de formato base64
	const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
	if (!base64Regex.test(data.dataB64)) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "malformed_base64",
			seq: data.seq,
		});
		return;
	}

	// Verificação de tamanho do chunk (evitar chunks excessivamente grandes)
	const chunkSizeBytes = (data.dataB64.length * 3) / 4;
	const maxChunkSize = 5 * 1024 * 1024; // 5MB por chunk
	if (chunkSizeBytes > maxChunkSize) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "chunk_too_large",
			seq: data.seq,
			chunkSizeBytes,
		});
		return;
	}

	// Verificação se o chunk já foi recebido (evitar duplicatas)
	if (t.chunks[data.seq - 1] !== undefined) {
		debugLog("UPLOAD_CHUNK_WARNING", {
			transferId: data.transferId,
			warning: "duplicate_chunk",
			seq: data.seq,
		});
		// Ainda assim enviamos ACK para não travar o upload
		corelCommunication.sendActionGuarded("uploadLayoutChunkAck", {
			transferId: data.transferId,
			seq: data.seq,
		});
		return;
	}

	// Verificação de limite de chunks para evitar ataques de memória
	if (data.seq > 10000) {
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "seq_too_high",
			seq: data.seq,
		});
		return;
	}

	if (data.totalChunks && !t.expectedChunks)
		t.expectedChunks = data.totalChunks;
	t.chunks[data.seq - 1] = data.dataB64;
	t.receivedBytesApprox += chunkSizeBytes;

	// Verificação se o tamanho acumulado não excede o limite
	const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
	if (t.receivedBytesApprox > maxBytes * 1.1) {
		// 10% de tolerância para overhead do base64
		debugLog("UPLOAD_CHUNK_ERROR", {
			transferId: data.transferId,
			error: "accumulated_size_exceeded",
			receivedBytes: t.receivedBytesApprox,
			maxBytes,
		});
		inflight.delete(data.transferId);
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 413,
			message: "Tamanho acumulado excedido",
		});
		return;
	}

	// ACK por chunk (janela de controle pode ser ajustada no C#)
	corelCommunication.sendActionGuarded("uploadLayoutChunkAck", {
		transferId: data.transferId,
		seq: data.seq,
	});
}

/**
 * Trata a mensagem de conclusão (end) e agenda o processamento final com controle de concorrência.
 */
function handleEnd(data: EndData) {
	const t = inflight.get(data.transferId);
	if (!t) {
		debugLog("UPLOAD_END_ERROR", {
			transferId: data.transferId,
			error: "transfer_not_found",
		});
		return;
	}

	// Validação do totalBytes
	if (!Number.isInteger(data.totalBytes) || data.totalBytes < 0) {
		debugLog("UPLOAD_END_ERROR", {
			transferId: data.transferId,
			error: "invalid_total_bytes",
			totalBytes: data.totalBytes,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "Total de bytes inválido",
		});
		inflight.delete(data.transferId);
		return;
	}

	// Checagem de tamanho anunciado vs recebido
	if (data.totalBytes !== t.begin.totalBytes) {
		debugLog("UPLOAD_END_ERROR", {
			transferId: data.transferId,
			error: "size_mismatch",
			announced: data.totalBytes,
			expected: t.begin.totalBytes,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "Tamanho divergente (end)",
		});
		inflight.delete(data.transferId);
		return;
	}

	// Verificação de integridade dos chunks
	if (t.expectedChunks) {
		const receivedChunks = t.chunks.filter(
			(chunk) => chunk !== undefined,
		).length;
		if (receivedChunks !== t.expectedChunks) {
			debugLog("UPLOAD_END_ERROR", {
				transferId: data.transferId,
				error: "missing_chunks",
				received: receivedChunks,
				expected: t.expectedChunks,
			});
			corelCommunication.sendActionGuarded("uploadLayoutResult", {
				transferId: data.transferId,
				status: "error",
				apiStatus: 400,
				message: "Chunks faltando",
			});
			inflight.delete(data.transferId);
			return;
		}
	}

	// Verificação de timeout do upload
	const uploadDuration = performance.now() - t.startedAt;
	if (uploadDuration > UPLOAD_TIMEOUT_MS) {
		debugLog("UPLOAD_END_ERROR", {
			transferId: data.transferId,
			error: "upload_timeout",
			duration: uploadDuration,
			maxDuration: UPLOAD_TIMEOUT_MS,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 408,
			message: "Timeout do upload",
		});
		inflight.delete(data.transferId);
		return;
	}

	// Validação do SHA256 se fornecido
	if (data.sha256 && t.begin.sha256 && data.sha256 !== t.begin.sha256) {
		debugLog("UPLOAD_END_ERROR", {
			transferId: data.transferId,
			error: "sha256_mismatch",
			endSha256: data.sha256,
			beginSha256: t.begin.sha256,
		});
		corelCommunication.sendActionGuarded("uploadLayoutResult", {
			transferId: data.transferId,
			status: "error",
			apiStatus: 400,
			message: "Hash SHA256 divergente",
		});
		inflight.delete(data.transferId);
		return;
	}

	debugLog("UPLOAD_END_VALIDATED", {
		transferId: data.transferId,
		totalBytes: data.totalBytes,
		duration: uploadDuration,
	});

	// Agendar upload com controle de concorrência
	scheduleUpload(() => processCompleteTransfer(data.transferId));
}

/**
 * Inicializa a ponte de upload de layouts, registrando o handler de mensagens do WebView2
 * e emitindo um log de prontidão com parâmetros de configuração relevantes.
 */
export function initLayoutUploadBridge(): void {
	const handler = (event: MessageEvent) => {
		try {
			const payload =
				typeof event.data === "string" ? JSON.parse(event.data) : event.data;
			const action = payload?.action as string;
			const data = payload?.data;
			if (!action) return;

			switch (action) {
				case "uploadLayoutBegin":
					handleBegin(data as BeginData);
					break;
				case "uploadLayoutChunk":
					handleChunk(data as ChunkData);
					break;
				case "uploadLayoutEnd":
					handleEnd(data as EndData);
					break;
				default:
					break;
			}
		} catch (e) {
			debugLog("UPLOAD_BRIDGE_ERROR", { error: (e as any)?.message });
		}
	};

	try {
		(window as any)?.chrome?.webview?.addEventListener("message", handler);
	} catch {
		// fallback dev
		window.addEventListener("message", handler as any);
	}

	debugLog("UPLOAD_BRIDGE_READY", {
		API_URL,
		REQUEST_TIMEOUT_MS,
		UPLOAD_TIMEOUT_MS,
		MAX_FILE_SIZE_MB,
		MAX_PARALLEL_UPLOADS,
	});
}
